import { sel } from "../selectors";
import { DEFAULT_STAGE_VIEWPORT, SAMPLES_BASE, Scene } from "./types";

// Lesson: Getting started → Your first report.
// Fixture: `firstNarrativeReport` (a demo officer with two finished cases; the
// upload creates a third that the `completeLatestCase` mutation finishes with
// the draft written from the sample dictation).
const scene: Scene = {
  id: "first-narrative-report",
  title: "Your first report",
  startRoute: "/cases",
  fixture: "firstNarrativeReport",
  viewport: DEFAULT_STAGE_VIEWPORT,
  steps: [
    {
      id: "intro",
      narration:
        "This is the fastest way to get a report out of Police Narratives AI: record or upload what happened in your own words, and let the AI write the first draft. I'll walk through it with a sample recording you can download right under this player.",
      actions: [
        { type: "waitFor", target: sel("newCaseDropdown") },
        { type: "spotlight", target: null },
        { type: "wait", ms: 1500 },
      ],
    },
    {
      id: "new-case",
      narration:
        "Everything starts from the New Case button at the top of the sidebar. I'll pick Upload Files.",
      actions: [
        // Timed to the clip (5.4 s): "New Case button" is spoken around
        // 1.5 s, "I'll pick" starts at 3.7 s and "Upload Files" at 4.4 s, so
        // the click on Upload Files lands as those words are said.
        { type: "moveTo", target: `${sel("newCaseDropdown")} button` },
        { type: "wait", ms: 1100 },
        { type: "click", target: `${sel("newCaseDropdown")} button` },
        { type: "waitFor", target: sel("newCaseUpload") },
        { type: "wait", ms: 600 },
        { type: "moveTo", target: sel("newCaseUpload") },
        { type: "wait", ms: 600 },
        { type: "click", target: sel("newCaseUpload") },
        { type: "waitFor", target: sel("uploadDropzone") },
      ],
    },
    {
      id: "drop-file",
      narration:
        "Drop your audio in here, or click to browse. This is the sample dictation. Each file gets a type: Narrative means it's you describing what happened, so I'll leave that selected.",
      actions: [
        { type: "moveTo", target: sel("uploadDropzone") },
        { type: "wait", ms: 900 },
        {
          type: "attachFile",
          target: `${sel("uploadDropzone")} input[type="file"]`,
          url: `${SAMPLES_BASE}/narrative-retail-theft.mp3`,
          name: "narrative-retail-theft.mp3",
          mime: "audio/mpeg",
        },
        { type: "waitFor", target: sel("uploadTypeNarrative") },
        { type: "wait", ms: 2500 },
        { type: "moveTo", target: sel("uploadTypeNarrative") },
        { type: "spotlight", target: sel("uploadTypeNarrative") },
        { type: "wait", ms: 2000 },
        { type: "spotlight", target: null },
      ],
    },
    {
      id: "report-settings",
      narration:
        "Report Settings is where you pick a template, the detail level, and whether to include timestamps. The defaults come from your preferences, so most of the time you can leave this alone.",
      actions: [
        { type: "scrollIntoView", target: sel("uploadReportSettings") },
        { type: "click", target: sel("uploadReportSettings") },
        { type: "wait", ms: 4500 },
        { type: "click", target: sel("uploadReportSettings") },
        { type: "wait", ms: 400 },
      ],
    },
    {
      id: "upload",
      narration:
        "Hit Upload. The file goes up, gets transcribed, and the draft is written from your preferences. You can leave the page while this runs; it'll be waiting in your case list.",
      actions: [
        { type: "scrollIntoView", target: sel("uploadSubmit") },
        { type: "click", target: sel("uploadSubmit") },
        { type: "waitFor", target: sel("editorProcessing"), timeoutMs: 15000 },
        { type: "wait", ms: 4000 },
        { type: "fixture", name: "completeLatestCase" },
      ],
    },
    {
      id: "draft",
      narration:
        "Here's the draft, in the editor, written in the style you set up. The score on the right tells you how complete it is, and the suggestions underneath are the details the AI thinks a supervisor would ask about.",
      actions: [
        { type: "waitFor", target: sel("reportScore"), timeoutMs: 15000 },
        { type: "wait", ms: 2500 },
        { type: "moveTo", target: sel("reportScore") },
        { type: "spotlight", target: sel("reportScore") },
        { type: "wait", ms: 3000 },
        { type: "moveTo", target: sel("suggestionItem") },
        { type: "spotlight", target: sel("suggestionItem") },
        { type: "wait", ms: 3000 },
        { type: "spotlight", target: null },
      ],
    },
    {
      id: "outro",
      narration:
        "That's the whole loop. Next lesson covers how to work through those suggestions, fix placeholders, and export the finished report.",
      actions: [
        { type: "moveTo", target: sel("editorNarrative") },
        { type: "wait", ms: 1500 },
      ],
    },
  ],
};

export default scene;
